import React, { useEffect, useRef } from "react";

import { dataAttrTestId } from "@at/formatter-utils/lib/data-attr-utils/data-attr-utils";

import {
  GalleryGridModalMainImage,
  GalleryGridModalThumbnailImage,
} from "./gallery-modal-grid-images";
import { GalleryModalGridStyles } from "./gallery-modal-grid.styled";
import {
  comScorePageViewTrack,
  ImageGalleryState,
} from "../../../utils/tracking/tracking-data-utils";
import { useTracking } from "../../../utils/tracking/use-tracking";
import {
  GalleryImage,
  ImageInViewHandler,
} from "../gallery-image/gallery-image";
import { type GalleryAsset, groupByCategory } from "../gallery.utils";

interface GalleryGridProps {
  slide: number;
  images: GalleryAsset[];
  onImageClick: (index: number) => void;
  activeCategory: string;
  handleImageTracked: (
    imageId: string,
    index: number,
    type: ImageGalleryState,
  ) => void;
}

export const GalleryModalGrid = ({
  slide,
  images,
  onImageClick,
  activeCategory,
  handleImageTracked,
}: GalleryGridProps) => {
  const { trackEvent } = useTracking();
  const scrollableList = useRef<HTMLUListElement>(null);

  const groupedImages = groupByCategory(images);

  const onImageInView: ImageInViewHandler = ({ index, src }) => {
    const imageId = src.match(/([\dA-Za-z]+)\.jpg/)?.[1] || src;

    handleImageTracked(imageId, index, ImageGalleryState.in_grid);

    // Triggers every 6th image view
    if ((index + 1) % 6 === 0) {
      comScorePageViewTrack();
    }
  };

  useEffect(() => {
    const child = scrollableList?.current?.children[slide] as HTMLElement;
    if (slide !== 0 && child && child.scrollIntoView) {
      child.scrollIntoView({
        behavior: "instant" as globalThis.ScrollBehavior,
      });
    }
  }, [slide]);

  if (images.length === 0) {
    return null;
  }

  return (
    <GalleryModalGridStyles {...{ [dataAttrTestId]: "image-grid-component" }}>
      <ul ref={scrollableList}>
        {groupedImages[activeCategory]?.map((image, index) => {
          const src = image.image.url;
          const alt = image.image.alt;
          const isFocusImage = index % 3 === 0;
          return (
            <li key={`image-container-${index}`}>
              <GalleryImage
                {...{
                  src,
                  index,
                  onClick: () => {
                    trackEvent({ trackingLabel: "open-fullscreen-carousel" });
                    onImageClick(index);
                  },
                  onInView: onImageInView,
                  dataTestId: `open-carousel-${index}`,
                }}
              >
                {isFocusImage ? (
                  <GalleryGridModalMainImage src={src} alt={alt} />
                ) : (
                  <GalleryGridModalThumbnailImage src={src} alt={alt} />
                )}
              </GalleryImage>
            </li>
          );
        })}
      </ul>
    </GalleryModalGridStyles>
  );
};
